import { ChromePicker, ColorResult, RgbColor } from "@hello-pangea/color-picker";
import { Box, Divider } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import { useEffect, useMemo, useRef, useState } from "react";

import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import SubscriptIcon from "@mui/icons-material/Subscript";
import SuperscriptIcon from "@mui/icons-material/Superscript";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

import FormatIndentDecreaseIcon from "@mui/icons-material/FormatIndentDecrease";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncrease";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { ROOT_NODE, useEditor } from "@craftjs/core";

import StyledToggleButtonGroup from "@/components/PageEditor/components/Viewport/components/Toolbar/components/StyledToggleButtonGroup/StyledToggleButtonGroup";
import LinkPopper from "@/components/PageEditor/editor-components/shared/LinkPopper";
import { ElementDatabaseService, PageService } from "@/services/admin-services";
import { DatabaseElement, Page } from "@/services/admin-services/models";
import { ColorUtils } from "@/utils/ColorUtils";
import { SelectionUtils } from "@/utils/SelectionUtils";

const Toolbar = () => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const [alignment, setAlignment] = useState("left");
    const [formats, setFormats] = useState<Array<string>>([]);
    const [list, setList] = useState<string>("");

    const colorPickerActiveRef = useRef<boolean>(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState<boolean>(false);
    const [textColor, setTextColor] = useState<RgbColor>({ r: 0, g: 0, b: 0, a: 1 });
    const [showFillColorPicker, setShowFillColorPicker] = useState<boolean>(false);
    const [fillColor, setFillColor] = useState<RgbColor>({ r: 0, g: 0, b: 0, a: 0 });

    const [showAnchorPopper, setShowAnchorPopper] = useState<boolean>(false);
    const selectedAnchorRef = useRef<HTMLAnchorElement | null>(null);

    const selectionUtils = useRef<SelectionUtils>(new SelectionUtils());

    const pageService = useMemo(() => new PageService("int"), []);
    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);

    const [pages, setPages] = useState<Array<Page>>();
    const [availablePages, setAvailablePages] = useState<Array<Page>>([]);
    const [elements, setElements] = useState<Array<DatabaseElement>>([]);

    const { language } = useEditor((state) => ({
        language: state.nodes[ROOT_NODE].data.props.language,
    }));

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        document.execCommand("styleWithCSS", false, true as any);
        document.addEventListener("selectionchange", onSelectionChange, true);

        pageService.getPages().then(setPages);
        elementService.get().then(setElements);

        return () => {
            // Remove any leftover highlights from the editor
            const links = document.querySelectorAll(".craftjs-renderer a");
            links.forEach((link) => ((link as HTMLAnchorElement).style.backgroundColor = ""));

            document.removeEventListener("selectionchange", onSelectionChange, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!pages) return;

        setAvailablePages(pages.filter((p) => p.language === language));
    }, [language, pages]);

    useEffect(() => {
        colorPickerActiveRef.current = showTextColorPicker || showFillColorPicker;
    }, [showTextColorPicker, showFillColorPicker]);

    const onSelectionChange = () => {
        if (colorPickerActiveRef.current) return;

        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const baseNode = activeSelection.anchorNode as HTMLElement;
        if (!baseNode) return;

        const baseNodeParent = baseNode.parentElement as HTMLElement;
        if (
            (baseNode.closest && baseNode.closest(".craftjs-toolbar")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".craftjs-toolbar")) ||
            (baseNode.closest && baseNode.closest(".chrome-picker")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".chrome-picker")) ||
            (baseNode.closest && baseNode.closest(".link-popper")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".link-popper"))
        ) {
            if (!colorPickerActiveRef.current) {
                setEnabled(false);
            }

            return;
        }

        setEnabled(true);

        if ((activeSelection.type === "Range" || activeSelection.type === "Caret") && activeSelection.rangeCount > 0) {
            selectionUtils.current.save();
        } else if (activeSelection.type) {
            selectionUtils.current.clearSaved();
        }

        updateState();
    };

    const updateState = () => {
        updateAlignment();
        updateFormats();
        updateColors();
        updateLists();
        updateLink();
    };

    const updateAlignment = () => {
        const left = document.queryCommandState("justifyLeft");
        const center = document.queryCommandState("justifyCenter");
        const right = document.queryCommandState("justifyRight");
        const full = document.queryCommandState("justifyFull");

        if (left) setAlignment("left");
        else if (center) setAlignment("center");
        else if (right) setAlignment("right");
        else if (full) setAlignment("justify");
    };

    const updateFormats = () => {
        const bold = document.queryCommandState("bold");
        const italic = document.queryCommandState("italic");
        const underline = document.queryCommandState("underline");
        const strikeThrough = document.queryCommandState("strikeThrough");
        const subscript = document.queryCommandState("subscript");
        const superscript = document.queryCommandState("superscript");

        const selection = [];
        if (bold) selection.push("bold");
        if (italic) selection.push("italic");
        if (underline) selection.push("underline");
        if (strikeThrough) selection.push("strikeThrough");
        if (subscript) selection.push("subscript");
        if (superscript) selection.push("superscript");

        setFormats(selection);
    };

    const updateColors = () => {
        if (colorPickerActiveRef.current) return;

        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const element = activeSelection.anchorNode?.parentNode as HTMLElement;
        if (!element) return;

        const color = getStyleProperty(element, "color");
        const backgroundColor = getStyleProperty(element, "background-color");

        setTextColor(ColorUtils.toRgba(color));
        setFillColor(ColorUtils.toRgba(backgroundColor));
    };

    const getStyleProperty = (element: HTMLElement, property: string): string => {
        const style = window.getComputedStyle(element);
        const value = style.getPropertyValue(property);

        if (value !== "rgba(0, 0, 0, 0)") return value;
        if (!element.parentElement) return value;
        if (element.classList.contains("component-selected")) return value;

        return getStyleProperty(element.parentElement, property);
    };

    const updateLists = () => {
        const ul = document.queryCommandState("insertUnorderedList");
        const ol = document.queryCommandState("insertOrderedList");

        if (ul) setList("ul");
        else if (ol) setList("ol");
        else setList("");
    };

    const updateLink = () => {
        const links = document.querySelectorAll(".craftjs-renderer a");
        links.forEach((link) => ((link as HTMLAnchorElement).style.backgroundColor = ""));

        let anchorTag = selectionUtils.current.findParentTag("A") as HTMLAnchorElement | null;
        if (anchorTag) {
            if (anchorTag.closest(".craftjs-renderer")) {
                anchorTag.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
            } else {
                anchorTag = null;
            }
        }

        selectedAnchorRef.current = anchorTag;
        setShowAnchorPopper(Boolean(anchorTag));
    };

    const onToggleCommand = (cmd: string, showUi: boolean = false, value: string | undefined = undefined) => {
        selectionUtils.current.restore();
        document.execCommand(cmd, showUi, value);
        updateState();
    };

    const onCreateLink = () => {
        setShowAnchorPopper(true);
    };

    const onEditLink = (href: string, value: string, openInNewTab: boolean, data: Record<string, string>) => {
        document.designMode = "on";

        setShowAnchorPopper(false);

        selectionUtils.current.restore();
        const anchorTag = selectionUtils.current.findParentTag("A") as HTMLAnchorElement;

        if (anchorTag) {
            selectionUtils.current.expandToTag(anchorTag);

            anchorTag.setAttribute("href", href);
            anchorTag.innerText = value || href;
            anchorTag.target = openInNewTab ? "_blank" : "_self";
        } else {
            document.execCommand("createLink", false, href);
        }

        const link = document.querySelector(`a[href="${href}"]`) as HTMLAnchorElement;
        if (link) {
            const selection = SelectionUtils.get();
            if (selection) {
                const selectedText = selection.getRangeAt(0).cloneContents().textContent;
                if (selectedText === href) {
                    link.innerHTML = value || href;
                }
            }

            link.target = openInNewTab ? "_blank" : "_self";

            if (data["internal-page-id"]) {
                const pageId = data["internal-page-id"];
                link.setAttribute("data-internal-page-id", pageId);
                link.removeAttribute("data-internal-element-slug");
            } else if (data["internal-element-slug"]) {
                const elementSlug = data["internal-element-slug"];
                link.setAttribute("data-internal-element-slug", elementSlug);
                link.removeAttribute("data-internal-page-id");
            } else {
                link.removeAttribute("data-internal-element-slug");
                link.removeAttribute("data-internal-page-id");
            }
        }

        document.designMode = "off";

        selectionUtils.current.collapseToEnd();
    };

    const onRemoveLink = () => {
        setShowAnchorPopper(false);

        const anchorTag = selectionUtils.current.findParentTag("A");
        if (!anchorTag) return;

        selectionUtils.current.expandToTag(anchorTag);
        anchorTag.style.backgroundColor = "";
        document.execCommand("unlink");

        selectionUtils.current.collapseToEnd();
    };

    return (
        <>
            <StyledToggleButtonGroup
                disabled={!enabled}
                size="small"
                value={alignment}
                exclusive
                aria-label="text alignment"
            >
                <ToggleButton
                    value="left"
                    aria-label="left aligned"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("justifyLeft");
                    }}
                >
                    <FormatAlignLeftIcon />
                </ToggleButton>
                <ToggleButton
                    value="center"
                    aria-label="centered"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("justifyCenter");
                    }}
                >
                    <FormatAlignCenterIcon />
                </ToggleButton>
                <ToggleButton
                    value="right"
                    aria-label="right aligned"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("justifyRight");
                    }}
                >
                    <FormatAlignRightIcon />
                </ToggleButton>
                <ToggleButton
                    value="justify"
                    aria-label="justified"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("justifyFull");
                    }}
                >
                    <FormatAlignJustifyIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

            <StyledToggleButtonGroup
                disabled={!enabled}
                size="small"
                value={formats}
                aria-label="text formatting"
                sx={{ flexWrap: "wrap" }}
            >
                <ToggleButton
                    value="bold"
                    aria-label="bold"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("bold");
                    }}
                >
                    <FormatBoldIcon />
                </ToggleButton>
                <ToggleButton
                    value="italic"
                    aria-label="italic"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("italic");
                    }}
                >
                    <FormatItalicIcon />
                </ToggleButton>
                <ToggleButton
                    value="underline"
                    aria-label="underlined"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("underline");
                    }}
                >
                    <FormatUnderlinedIcon />
                </ToggleButton>
                <ToggleButton
                    value="strikeThrough"
                    aria-label="strikeThrough"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("strikeThrough");
                    }}
                >
                    <FormatStrikethroughIcon />
                </ToggleButton>
                <ToggleButton
                    value="subscript"
                    aria-label="subscript"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("subscript");
                    }}
                >
                    <SubscriptIcon />
                </ToggleButton>
                <ToggleButton
                    value="superscript"
                    aria-label="superscript"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("superscript");
                    }}
                >
                    <SuperscriptIcon />
                </ToggleButton>
                <ToggleButton
                    value="color"
                    aria-label="color"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setShowTextColorPicker(true);
                    }}
                >
                    <Box sx={{ position: "relative", height: 0, width: 0 }}>
                        {showTextColorPicker && (
                            <Box
                                onMouseDown={(e) => e.stopPropagation()}
                                className="color-picker-overlay"
                                sx={{
                                    position: "absolute",
                                    zIndex: 99999,
                                    top: "calc(100% + 23px)",
                                    left: -7,
                                }}
                            >
                                <Box
                                    className="color-picker-background"
                                    sx={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowTextColorPicker(false);
                                        selectionUtils.current.restore();
                                    }}
                                />
                                <ChromePicker
                                    color={textColor}
                                    onChange={(color: ColorResult) => setTextColor(color.rgb)}
                                    onChangeComplete={(color: ColorResult) => {
                                        selectionUtils.current.restore();
                                        setTextColor(color.rgb);
                                        document.execCommand("foreColor", false, ColorUtils.rgbaToString(color.rgb));
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: "absolute",
                            left: 9,
                            top: 27,
                            height: 4,
                            width: 20,
                            border: "1px solid rgba(0, 0, 0, 0.2)",
                            boxSizing: "border-box",
                            backgroundColor: ColorUtils.rgbaToString(textColor),
                            ...(enabled ? { opacity: 1 } : { opacity: 0.25 }),
                        }}
                    />
                    <FormatColorTextIcon />
                    <ArrowDropDownIcon />
                </ToggleButton>
                <ToggleButton
                    value="color"
                    aria-label="color"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setShowFillColorPicker(true);
                    }}
                >
                    <Box sx={{ position: "relative", height: 0, width: 0 }}>
                        {showFillColorPicker && (
                            <Box
                                onMouseDown={(e) => e.stopPropagation()}
                                className="color-picker-overlay"
                                sx={{
                                    position: "absolute",
                                    zIndex: 99999,
                                    top: "calc(100% + 23px)",
                                    left: -7,
                                }}
                            >
                                <Box
                                    className="color-picker-background"
                                    sx={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowFillColorPicker(false);
                                        selectionUtils.current.restore();
                                    }}
                                />
                                <ChromePicker
                                    color={fillColor}
                                    onChange={(color: ColorResult) => setFillColor(color.rgb)}
                                    onChangeComplete={(color: ColorResult) => {
                                        selectionUtils.current.restore();
                                        setFillColor(color.rgb);
                                        document.execCommand("hiliteColor", false, ColorUtils.rgbaToString(color.rgb));
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: "absolute",
                            left: 9,
                            top: 27,
                            height: 4,
                            width: 20,
                            border: "1px solid rgba(0, 0, 0, 0.2)",
                            boxSizing: "border-box",
                            backgroundColor: ColorUtils.rgbaToString(fillColor),
                            ...(enabled ? { opacity: "unset" } : { opacity: 0.25 }),
                        }}
                    />
                    <FormatColorFillIcon />
                    <ArrowDropDownIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

            <StyledToggleButtonGroup disabled={!enabled} size="small" value={list} aria-label="lists" exclusive>
                <ToggleButton
                    value="ul"
                    aria-label="unordered list"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("insertUnorderedList");
                    }}
                >
                    <FormatListBulletedIcon />
                </ToggleButton>
                <ToggleButton
                    value="Numbered ol"
                    aria-label="ordered list"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("insertOrderedList");
                    }}
                >
                    <FormatListNumberedIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

            <StyledToggleButtonGroup disabled={!enabled} size="small" aria-label="indentation">
                <ToggleButton
                    value="indent"
                    aria-label="indent"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("indent");
                    }}
                >
                    <FormatIndentIncreaseIcon />
                </ToggleButton>
                <ToggleButton
                    value="outdent"
                    aria-label="outdent"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCommand("outdent");
                    }}
                >
                    <FormatIndentDecreaseIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

            <StyledToggleButtonGroup disabled={!enabled} size="small" aria-label="links">
                {!selectedAnchorRef.current ? (
                    <ToggleButton
                        value="insert link"
                        aria-label="insert link"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onCreateLink();
                        }}
                    >
                        <LinkIcon />
                    </ToggleButton>
                ) : (
                    <ToggleButton
                        value="remove link"
                        aria-label="remove link"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onRemoveLink();
                        }}
                    >
                        <LinkOffIcon />
                    </ToggleButton>
                )}
            </StyledToggleButtonGroup>

            {showAnchorPopper && (
                <LinkPopper
                    active={showAnchorPopper}
                    anchorEl={selectedAnchorRef.current}
                    onEdit={onEditLink}
                    onRemove={onRemoveLink}
                    availablePages={availablePages}
                    availableElements={elements}
                />
            )}
        </>
    );
};

export default Toolbar;
